import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { HomeStackParamList, NotificationsScreenProps } from 'home/stack/types'
import { Notification } from 'notifications/types'
import NotificationsView from 'notifications/views/list'
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { confirmationAlert, errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { RetryQuery } from 'shared/queries/utils'
import { showStoreReview } from 'shared/store_review'
import { openLink } from 'shared/utils'
import { useRecentStoresContext } from 'stores/context'
import { showStoreHeaderDropdown } from 'stores/dropdown'
import { useStores } from 'stores/queries/list'
import { useUser } from 'users/queries/detail'
import { useNotifications } from './queries/list'
import { useMarkAllNotificationsRead } from './queries/mark_all_read'
import { useMarkNotificationRead } from './queries/mark_read'
import { isDummyNotificationRoute } from './utils'

export function NotificationsScreen({ navigation }: NotificationsScreenProps) {
  const { data: user } = useUser()
  const { handleNavigation } = useRoutingContext()
  const {
    activeStoreId,
    recentStores,
    setActiveStoreId,
    allStoresSelected,
    setAllStoresSelected,
  } = useRecentStoresContext()
  const { data: stores } = useStores(data => data.stores)
  const retryQuery: RetryQuery | undefined = undefined
  const selectedStoreId = allStoresSelected ? undefined : activeStoreId
  const notificationsQuery = useNotifications(retryQuery, selectedStoreId)
  const markNotificationReadMutation = useMarkNotificationRead()
  const markAllNotificationsReadMutation = useMarkAllNotificationsRead()
  const [markingAllRead, setMarkingAllRead] = useState<boolean>(false)

  const onPressMarkAllAsRead = useCallback(async () => {
    const confirmed = await confirmationAlert(t('confirmMarkAll'))

    if (!confirmed) {
      return
    }

    const notificationsPage = notificationsQuery.data?.pages[0]

    if (notificationsPage) {
      setMarkingAllRead(true)
      try {
        await markAllNotificationsReadMutation.mutateAsync({
          notifications: notificationsPage,
          storeId: selectedStoreId,
        })
        setMarkingAllRead(false)
      } catch (err: any) {
        setMarkingAllRead(false)
        errorAlert(err, t('markAllNotificationsReadError'))
      }
    }
  }, [
    markAllNotificationsReadMutation,
    notificationsQuery.data?.pages,
    selectedStoreId,
  ])

  const showStoreDropdown =
    user?.role === 'manager' ||
    user?.role === 'observer' ||
    user?.role === 'admin'
      ? true
      : false

  useLayoutEffect(() => {
    if (showStoreDropdown) {
      showStoreHeaderDropdown(navigation, handleNavigation, {
        activeStoreId,
        recentStores,
        stores,
        setAllStoresSelected,
        allStoresSelected,
        setActiveStoreId,
      })
    }
  }, [
    activeStoreId,
    allStoresSelected,
    handleNavigation,
    navigation,
    recentStores,
    setActiveStoreId,
    setAllStoresSelected,
    showStoreDropdown,
    stores,
  ])

  const onPressNotification = useCallback(
    async (notification: Notification) => {
      if (
        notification.type === 'MANAGER_INITIATED_COVER_SHIFT' ||
        notification.type === 'OPEN_SHIFT_CONFIRMED_BY_MANAGER'
      ) {
        showStoreReview()
      }
      markNotificationReadMutation.mutate({
        notification,
        storeId: selectedStoreId,
      })

      if (isDummyNotificationRoute(notification.route?.name)) {
        return
      }

      handleNavigation({
        routeName: notification.route.name as keyof HomeStackParamList,
        routeParams: notification.route.params && {
          ...notification.route.params,
        },
      })
    },
    [handleNavigation, markNotificationReadMutation, selectedStoreId]
  )

  const onNextPageNeeded = useCallback(async () => {
    if (notificationsQuery.hasNextPage) {
      return notificationsQuery.fetchNextPage()
    }
  }, [notificationsQuery])

  const onRefresh = useCallback(async () => {
    await notificationsQuery.refetch()
  }, [notificationsQuery])

  const onLinkPress = useCallback(
    async (url: string) => {
      if (user) {
        await openLink(url, navigation, user)
      }
    },
    [navigation, user]
  )

  const notifications = useMemo(
    () => notificationsQuery.data?.pages?.flatMap(page => page.notifications),
    [notificationsQuery.data]
  )

  const isUnreadNotification =
    notifications?.some(notification => !notification.read) ?? false

  const { isLoading, error } = notificationsQuery

  return (
    <StatusBoundary
      isLoading={isLoading || markingAllRead}
      error={error}
    >
      <NotificationsView
        notifications={notifications}
        onPressNotification={onPressNotification}
        onPressMarkAllAsRead={onPressMarkAllAsRead}
        onNextPageNeeded={onNextPageNeeded}
        onRefresh={onRefresh}
        isUnreadNotification={isUnreadNotification}
        onLinkPress={onLinkPress}
      />
    </StatusBoundary>
  )
}
